// 盤面の幾何（mch-dice の hex.js）。11行、奇数行7マス・偶数行6マス（半マスずらし）。
// マスは番号で扱う。各マスは表示用の (col,row) と距離計算用の axial (q,r) を持つ。
export const ROWS = 11;
export const DIRS: readonly [number, number][] = [
  [1, 0],
  [1, -1],
  [0, -1],
  [-1, 0],
  [-1, 1],
  [0, 1],
];

export interface Cell {
  i: number;
  col: number;
  row: number;
  q: number;
  r: number;
  n: number[]; // 隣接マス
}

export const rowWidth = (row: number) => (row % 2 ? 7 : 6);
export const axialQ = (col: number, row: number) => (row % 2 ? col - (row + 1) / 2 : col - row / 2);
/** 盤面の左端からの x（マス幅単位）。y は行 */
export const cellX = (col: number, row: number) => col + (row % 2 ? 0 : 0.5);

export function mirror(col: number, row: number): [number, number] {
  return [rowWidth(row) - 1 - col, ROWS - 1 - row];
}

function buildCells(): Cell[] {
  const cells: Cell[] = [];
  const byKey = new Map<string, number>();
  for (let row = 0; row < ROWS; row++) {
    for (let col = 0; col < rowWidth(row); col++) {
      const cell: Cell = { i: cells.length, col, row, q: axialQ(col, row), r: row, n: [] };
      byKey.set(`${cell.q},${cell.r}`, cell.i);
      cells.push(cell);
    }
  }
  for (const c of cells) {
    c.n = DIRS.map(([dq, dr]) => byKey.get(`${c.q + dq},${c.r + dr}`)).filter((v): v is number => v !== undefined);
  }
  return cells;
}

export const CELLS: readonly Cell[] = buildCells();
export const CELL_COUNT = CELLS.length;

const DIST: Uint8Array = (() => {
  const n = CELLS.length;
  const d = new Uint8Array(n * n);
  for (const a of CELLS)
    for (const b of CELLS) {
      const dq = a.q - b.q;
      const dr = a.r - b.r;
      d[a.i * n + b.i] = (Math.abs(dq) + Math.abs(dr) + Math.abs(dq + dr)) / 2;
    }
  return d;
})();

export const cellAt = (col: number, row: number): number | undefined => {
  if (row < 0 || row >= ROWS || col < 0 || col >= rowWidth(row)) return undefined;
  let i = 0;
  for (let r = 0; r < row; r++) i += rowWidth(r);
  return i + col;
};
export const dist = (a: number, b: number) => DIST[a * CELL_COUNT + b];
export const neighbors = (i: number) => CELLS[i].n;
export const adjacent = (a: number, b: number) => a !== b && DIST[a * CELL_COUNT + b] === 1;

/** a から b へ向かう方向（6方向のうち最も近いもの） */
export function dirToward(a: number, b: number): number {
  const A = CELLS[a];
  const B = CELLS[b];
  const toXY = (q: number, r: number) => [q + r / 2, r * 0.866];
  const [tx, ty] = toXY(B.q - A.q, B.r - A.r);
  let best = 0;
  let bestDot = -Infinity;
  DIRS.forEach(([q, r], d) => {
    const [x, y] = toXY(q, r);
    const dot = x * tx + y * ty;
    if (dot > bestDot) {
      bestDot = dot;
      best = d;
    }
  });
  return best;
}

export function step(i: number, d: number): number | undefined {
  const c = CELLS[i];
  const [dq, dr] = DIRS[((d % 6) + 6) % 6];
  return CELLS.find((o) => o.q === c.q + dq && o.r === c.r + dr)?.i;
}

/** b が a から6方向のどれかの直線の上にある */
export function onLine(a: number, b: number): boolean {
  const dq = CELLS[b].q - CELLS[a].q;
  const dr = CELLS[b].r - CELLS[a].r;
  return a !== b && (dq === 0 || dr === 0 || dq + dr === 0);
}
